import {
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationControlType,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';
import { CombinedOwnershipModel } from './combined-ownership.model';

export class CombinedOwnershipConfiguration extends ReportSectionConfigurationBase {
  typeName = 'CombinedOwnershipConfiguration';
  nodataLocalizationKeyName = 'combinedOwnership';
  readonly reportSection = ReportSection.CombinedOwnership;
  readonly applicableReportTypes = [ReportType.Contact, ReportType.Institution];
  readonly reportViewModelSections = [ReportViewModelSection.InvestorProfile_CombinedOwnership];

  get minSize(): number {
    const gridSection = this.availableDataSections.find(section => section.name === 'combinedOwnershipGrid');
    if (gridSection && gridSection.isSelected) {
      const totalUnits = gridSection.subSections
        ?.filter(subSection => subSection.isSelected)
        ?.reduce((sum, subSection) => sum + (subSection.unit || 0), 0);
      if (totalUnits && totalUnits > 10.5) {
        return 3;
      } else if (totalUnits && totalUnits >= 5.5) {
        return 2;
      }
    }
    return 1;
  }

  maxSize = 4;
  defaultSize = 4;
  readonly availableDataSections = [
    {
      name: 'combinedOwnershipShares',
      displayName: 'Shares (combined)',
      isSelected: true,
      canConfigure: true
    },
    {
      name: 'combinedOwnershipPctSO',
      displayName: '% S/O (combined)',
      isSelected: true,
      canConfigure: true
    },
    {
      name: 'combinedOwnershipHolderRank',
      displayName: 'Holder Rank (combined)',
      isSelected: true,
      canConfigure: true
    },
    {
      name: 'combinedOwnershipGrid',
      displayName: 'Grid',
      isSelected: true,
      canConfigure: true,
      type: ReportSectionConfigurationControlType.Table,
      subSections: [
        {
          name: 'combinedOwnershipGridSecurityName',
          displayName: 'Security Name',
          isSelected: true,
          canConfigure: false,
          unit: 3,
          property: 'ticker',
          isStatic: true,
          type: ReportSectionConfigurationControlType.Text
        },
        {
          name: 'combinedOwnershipGridConversion',
          displayName: 'Conversion',
          isSelected: true,
          canConfigure: true,
          unit: 2,
          property: 'type',
          type: ReportSectionConfigurationControlType.Custom
        },
        {
          name: 'combinedOwnershipGridShares',
          displayName: 'Shares',
          isSelected: true,
          canConfigure: true,
          unit: 2,
          property: 'id',
          type: ReportSectionConfigurationControlType.Custom
        },
        {
          name: 'combinedOwnershipGridShareChange',
          displayName: 'Share Change',
          isSelected: true,
          canConfigure: true,
          unit: 2,
          property: 'id',
          type: ReportSectionConfigurationControlType.Custom
        },
        {
          name: 'combinedOwnershipGridPctSO',
          displayName: '% S/O',
          isSelected: true,
          canConfigure: true,
          unit: 1,
          property: 'id',
          type: ReportSectionConfigurationControlType.Custom
        },
        {
          name: 'combinedOwnershipGridHolderRank',
          displayName: 'Current Holder Rank',
          isSelected: true,
          canConfigure: true,
          unit: 1.5,
          property: 'id',
          type: ReportSectionConfigurationControlType.Custom
        },
        {
          name: 'combinedOwnershipGridReportedDate',
          displayName: 'Reported Date',
          isSelected: true,
          canConfigure: true,
          unit: 1.5,
          property: 'id',
          type: ReportSectionConfigurationControlType.Date
        }
      ]
    }
  ];

  get name(): string {
    if (this.appliedReportType === ReportType.Contact) {
      return 'contactCombinedOwnership';
    } else if (this.appliedReportType === ReportType.Institution) {
      return 'institutionCombinedOwnership';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get displayName(): string {
    if (this.appliedReportType === ReportType.Contact || this.appliedReportType === ReportType.Institution) {
      return 'Combined Ownership';
    } else {
      throw new Error('Report Type not supported');
    }
  }
}

export interface CombinedOwnershipDetailsData {
  [ReportViewModelSection.InvestorProfile_CombinedOwnership]: CombinedOwnershipModel;
}
